import { Box, Flex, Grid, Label, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { memo, useMemo } from 'react';

import { messages } from './BudgetAmountsInfo.messages';
import { BudgetAmountsInfoProps } from './BudgetAmountsInfo.types';

export const BudgetAmountsInfo = memo<BudgetAmountsInfoProps>((props) => {
    const { budgetAmounts, budgetCurrency, period, requestFriendlyName } = props;

    const budgetCards = useMemo(
        () => [
            {
                label: messages.bills,
                approvedAmount: budgetAmounts.billApprovedAmount,
                submittedAmount: budgetAmounts.billSubmittedAmount,
                onReviewAmount: budgetAmounts.billOnReviewAmount,
            },
            {
                label: messages.purchaseOrders,
                approvedAmount: budgetAmounts.poApprovedAmount,
                submittedAmount: budgetAmounts.poSubmittedAmount,
                onReviewAmount: budgetAmounts.poOnReviewAmount,
            },
            {
                label: messages.expenses,
                approvedAmount: budgetAmounts.expensesApprovedAmount,
                submittedAmount: budgetAmounts.expensesSubmittedAmount,
                onReviewAmount: budgetAmounts.expensesOnReviewAmount,
            },
        ],
        [budgetAmounts]
    );

    return (
        <Grid rowGap={16}>
            <Box bordered borderColor='midnight50' radius='small' spacing='8 16'>
                <Flex spacing='16'>
                    <Grid rowGap={4}>
                        <Label>{messages.period}</Label>

                        <Text font='label' fontSize='medium'>
                            {period}
                        </Text>
                    </Grid>

                    <Grid rowGap={4}>
                        <Label>{messages.budget}</Label>

                        <Text font='label' fontSize='medium'>
                            {intl.formatCurrency(budgetAmounts.plannedAmount, budgetCurrency)}
                        </Text>
                    </Grid>

                    <Grid rowGap={4}>
                        <Label>{messages.remaining}</Label>

                        <Text font='label' fontSize='medium'>
                            {intl.formatCurrency(budgetAmounts.remainingAmount, budgetCurrency)}
                        </Text>
                    </Grid>
                </Flex>
            </Box>

            <Flex spacing='16' wrap>
                {budgetCards.map(({ label, approvedAmount, submittedAmount, onReviewAmount }) => (
                    <Box key={label} color='silver80' bordered radius='small' width='calc(50% - 8px)' spacing='12'>
                        <Grid rowGap={12}>
                            <Text font='label' fontSize='medium' fontWeight='medium'>
                                {label}
                            </Text>

                            <Flex spacing='16'>
                                <Grid rowGap={4}>
                                    <Label>{messages.approved}</Label>

                                    <Text font='label' fontSize='small' fontWeight='bold'>
                                        {intl.formatCurrency(approvedAmount, budgetCurrency)}
                                    </Text>
                                </Grid>

                                <Grid rowGap={4}>
                                    <Label>{messages.onApproval}</Label>

                                    <Text font='label' fontSize='small' fontWeight='bold'>
                                        {intl.formatCurrency(submittedAmount, budgetCurrency)}
                                    </Text>
                                </Grid>

                                <Grid rowGap={4}>
                                    <Label>{messages.onReview}</Label>

                                    <Text font='label' fontSize='small' fontWeight='bold'>
                                        {intl.formatCurrency(onReviewAmount, budgetCurrency)}
                                    </Text>
                                </Grid>
                            </Flex>
                        </Grid>
                    </Box>
                ))}
            </Flex>

            <Text font='label' fontSize='small' color='midnight70'>
                {messages.note({ requestFriendlyName })}
            </Text>
        </Grid>
    );
});

BudgetAmountsInfo.displayName = 'BudgetAmountsInfo';
